import { playerState } from "./playerStore.svelte.js";
import { projectState } from "./projectStore.svelte.js";

class AudioScrubManager {
    constructor() {
        this.audioElement = null;
        this.isEnabled = $state(true);
        this.isInitialized = $state(false);
        this.scrubTimeout = null;
        this.volume = $state(0.9); // 30% of main volume
    }

    /**
     * Initialize the audio scrubber with the current video source
     */
    initialize() {
        if (this.isInitialized || !window.player?.src) {
            return;
        }

        try {
            this.audioElement = new Audio();
            this.audioElement.src = projectState.media.localPath || window.player.src.src;
            this.audioElement.preload = 'metadata';
            this.audioElement.volume = this.volume;
            
            // Prevent buffering beyond what's needed
            this.audioElement.addEventListener('loadedmetadata', () => {
                this.audioElement.pause();
            });

            // Handle audio loading errors gracefully
            this.audioElement.addEventListener('error', (e) => {
                console.log(e.message);
                console.warn('Audio scrubber failed to load:', e);
                this.cleanup();
            });

            this.isInitialized = true;
            console.log('Audio scrubber initialized');
        } catch (error) {
            console.error('Failed to initialize audio scrubber:', error);
            console.log(error.message);
            this.cleanup();
        }
    }

    /**
     * Update the audio source when video source changes
     */
    updateSource(newSrc) {
        if (newSrc && this.audioElement && this.audioElement.src !== newSrc) {
            this.audioElement.src = newSrc;
            console.log('Audio scrubber source updated');
        }
    }

    /**
     * Perform audio scrubbing at the current video position
     */
    scrub() {
        // Only scrub if enabled, initialized, not playing, and local storage
        if (!this.isEnabled || 
            !this.isInitialized || 
            !this.audioElement || 
            playerState.playing ||
            !projectState.media ||
            projectState.media.storage !== "Local Storage") {
            return;
        }        
        
        try {
            // Clear any existing timeout
            if (this.scrubTimeout) {
                clearTimeout(this.scrubTimeout);
                this.scrubTimeout = null;
            }

            // Sync audio position to video position
            this.audioElement.currentTime = window.player.currentTime;
            
            // Calculate scrub duration (170% of frame duration)
            const frameDuration = 1000 / (projectState.frameRate || 29.97);
            const scrubDuration = frameDuration * 1.7;

            // Play audio briefly
            const playPromise = this.audioElement.play();
            
            if (playPromise !== undefined) {
                playPromise.then(() => {
                    // Audio started playing successfully, set up auto-pause
                    this.scrubTimeout = setTimeout(() => {
                        if (this.audioElement) {
                            this.audioElement.pause();
                        }

                        this.scrubTimeout = null;
                    }, scrubDuration);
                }).catch(error => {
                    console.warn('Audio scrub play failed:', error);
                    // Clear any timeout that might have been set
                    if (this.scrubTimeout) {
                        clearTimeout(this.scrubTimeout);
                        this.scrubTimeout = null;
                    }
                });
            } else {
                // Fallback for browsers that don't return a promise
                this.scrubTimeout = setTimeout(() => {
                    if (this.audioElement) {
                        this.audioElement.pause();
                    }
                    this.scrubTimeout = null;
                }, scrubDuration);
            }

        } catch (error) {
            console.warn('Audio scrub error:', error);
            if (this.scrubTimeout) {
                clearTimeout(this.scrubTimeout);
                this.scrubTimeout = null;
            }
        }
    }

    /**
     * Update the scrubbing volume
     */
    setVolume(mainVolume) {
        this.volume = mainVolume * 0.9; // 9% of main volume
        if (this.audioElement) {
            this.audioElement.volume = this.volume;
        }
    }

    /**
     * Toggle audio scrubbing on/off
     */
    toggle() {
        this.isEnabled = !this.isEnabled;
        if (!this.isEnabled && this.audioElement) {
            this.audioElement.pause();
        }
    }

    /**
     * Clean up audio resources
     */
    cleanup() {
        if (this.scrubTimeout) {
            clearTimeout(this.scrubTimeout);
            this.scrubTimeout = null;
        }

        if (this.audioElement) {
            this.audioElement.pause();
            this.audioElement.src = '';
            this.audioElement.load(); // Force cleanup
            this.audioElement = null;
        }

        this.isInitialized = false;
        console.log('Audio scrubber cleaned up');
    }

    /**
     * Check if scrubbing should be available
     */
    get shouldBeAvailable() {
        return projectState.media && 
               projectState.media.storage === "Local Storage" && 
               window.player?.src;
    }
}

// Create singleton instance
const audioScrubManager = new AudioScrubManager();

// Export the main function and utilities
export function audioScrub() {
    audioScrubManager.scrub();
}

export function initializeAudioScrub() {
    if (audioScrubManager.shouldBeAvailable) {
        audioScrubManager.initialize();
    }
}

export function updateAudioScrubSource(src) {
    audioScrubManager.updateSource(src);
}

export function setAudioScrubVolume(volume) {
    audioScrubManager.setVolume(volume);
}

export function toggleAudioScrub() {
    audioScrubManager.toggle();
    return audioScrubManager.isEnabled;
}

export function cleanupAudioScrub() {
    audioScrubManager.cleanup();
}

export function getAudioScrubState() {
    return {
        isEnabled: audioScrubManager.isEnabled,
        isInitialized: audioScrubManager.isInitialized,
        shouldBeAvailable: audioScrubManager.shouldBeAvailable
    };
}
